import { Data, ErrorsType, FormDetails, InputError } from "@/types";
import { initialInputErrors, inputErrorsReducer } from "@/reducers";
import { useContext, useEffect, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { useLocation, useNavigate } from "react-router-dom";
import { FieldValues, Resolver, useForm } from "react-hook-form";
import useWindowDimensions from "@/hooks/useWindowDimensions";
import NonDesktopInputs from "./nonDesktopInputs";
import DesktopInputs from "./desktopInputs";
import DetailsInputs from "./detailsInputs";
import Explanation from "./explanation";
import AppContext from "@/context/appContext";
import ReCAPTCHA from "react-google-recaptcha";
import Divider from "./divider";
import Button from "./button";
import * as yup from "yup";
import { ToastContainer, toast } from "react-toastify";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as
  | string
  | undefined;

export default function MainForm() {
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const { width } = useWindowDimensions();
  const isDesktopView = width ? width > 1024 : false;
  const { t } = useTranslation("main");
  const navigate = useNavigate();
  const location = useLocation();
  const captchaRef = useRef<ReCAPTCHA>(null);

  // RW-60: /report redirects here with this state when it was opened without usable input data
  // (e.g. deep-link / new tab, now that form state is per-tab sessionStorage). Show a privacy-
  // positive notice and move focus to the first field so the user knows why the form is empty.
  const privacyRedirect =
    (location.state as { privacyRedirect?: boolean } | null)?.privacyRedirect ??
    false;
  const [privacyNotice, setPrivacyNotice] = useState("");

  useEffect(() => {
    if (privacyRedirect) {
      document.getElementById("firstName")?.focus();
    }
  }, [privacyRedirect]);

  // Populate the live-region text AFTER mount (not at first paint) so a screen reader registers it
  // as a content change into an already-present region and reliably announces it on the client-side
  // redirect — WCAG 4.1.3 Status Messages (per the Stage 3 accessibility review).
  useEffect(() => {
    if (privacyRedirect) {
      setPrivacyNotice(t("privacy_redirect_notice"));
    }
  }, [privacyRedirect, t]);

  const { inputData, setInputData } = useContext(AppContext);

  const [inputErrors, setInputErrors] = useReducer(
    inputErrorsReducer,
    initialInputErrors,
  );

  const {
    strategicNumberOfProcurements,
    strategicAverageContractValue,
    strategicNumberOfEvaluators,
    strategicAverageDuration,
    majorNumberOfProcurements,
    majorAverageContractValue,
    majorNumberOfEvaluators,
    majorAverageDuration,
  } = inputData || {};

  useEffect(() => {
    if (
      typeof majorAverageContractValue === "number" &&
      majorAverageContractValue > 0 &&
      typeof majorAverageDuration === "number" &&
      majorAverageDuration > 0 &&
      typeof majorNumberOfEvaluators === "number" &&
      majorNumberOfEvaluators > 0 &&
      typeof majorNumberOfProcurements === "number" &&
      majorNumberOfProcurements > 0 &&
      typeof strategicAverageContractValue === "number" &&
      strategicAverageContractValue > 0 &&
      typeof strategicAverageDuration === "number" &&
      strategicAverageDuration > 0 &&
      typeof strategicNumberOfEvaluators === "number" &&
      strategicNumberOfEvaluators > 0 &&
      typeof strategicNumberOfProcurements === "number" &&
      strategicNumberOfProcurements > 0 &&
      inputErrors.strategicErrors.every((error) => error === "") &&
      inputErrors.majorErrors.every((error) => error === "")
    ) {
      setIsButtonEnabled(true);
    } else setIsButtonEnabled(false);
  }, [
    majorAverageContractValue,
    majorAverageDuration,
    majorNumberOfEvaluators,
    majorNumberOfProcurements,
    strategicAverageContractValue,
    strategicAverageDuration,
    strategicNumberOfEvaluators,
    strategicNumberOfProcurements,
    inputErrors.majorErrors,
    inputErrors.strategicErrors,
  ]);

  const formDetails: FormDetails[] = [
    {
      title: t("number_of_procurements"),
      placeholder: "-",
      border: true,
      inputId1: "strategicNumberOfProcurements",
      inputId2: "majorNumberOfProcurements",
      errorsType1: "strategicErrors",
      errorsType2: "majorErrors",
    },
    {
      title: t("average_value"),
      placeholder: "£",
      border: true,
      inputId1: "strategicAverageContractValue",
      inputId2: "majorAverageContractValue",
      errorsType1: "strategicErrors",
      errorsType2: "majorErrors",
    },
    {
      title: t("number_of_evaluators"),
      placeholder: "-",
      border: true,
      inputId1: "strategicNumberOfEvaluators",
      inputId2: "majorNumberOfEvaluators",
      errorsType1: "strategicErrors",
      errorsType2: "majorErrors",
    },
    {
      title: t("average_duration"),
      placeholder: t("days_placeholder"),
      border: false,
      inputId1: "strategicAverageDuration",
      inputId2: "majorAverageDuration",
      errorsType1: "strategicErrors",
      errorsType2: "majorErrors",
    },
  ];

  const schema = yup.object().shape({
    firstName: yup.string().required(t("form.required") as string),
    lastName: yup.string().required(t("form.required") as string),
    organisation: yup.string().required(t("form.required") as string),
    email: yup
      .string()
      .matches(/^[^@]+@[^@]+\.[^@]+$/, {
        message: t("form.invalid_email"),
        excludeEmptyString: true,
      })
      .required(t("form.required") as string),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FieldValues>({
    // The shared Input/DetailsInputs chain is typed against the loose FieldValues;
    // cast the concrete yup-inferred resolver to match (the schema validates the
    // same firstName/lastName/email/organisation fields).
    resolver: yupResolver(schema) as unknown as Resolver<FieldValues>,
  });

  const handleFormSubmit = handleSubmit(async () => {
    let captchaToken: string | null = null;
    if (RECAPTCHA_SITE_KEY) {
      try {
        captchaToken = (await captchaRef.current?.executeAsync()) ?? null;
      } catch (error) {
        console.error("reCAPTCHA executeAsync threw", error);
      }
      if (!captchaToken) {
        toast.error("Could not verify you are human. Please try again.", {
          toastId: "captcha_error",
        });
        return;
      }
    }
    navigate("/report", { state: { captchaToken } });
  });

  const setErrorMessage = (
    index: number,
    type: ErrorsType,
    message: InputError,
  ) => {
    setInputErrors({
      type,
      payload: {
        index,
        message,
      },
    });
  };

  const resetCalculator = () =>
    setInputData &&
    setInputData({
      ...inputData,
      strategicNumberOfProcurements: undefined,
      strategicAverageContractValue: undefined,
      strategicNumberOfEvaluators: undefined,
      strategicAverageDuration: undefined,
      majorNumberOfProcurements: undefined,
      majorAverageContractValue: undefined,
      majorNumberOfEvaluators: undefined,
      majorAverageDuration: undefined,
    } as Data);

  const props = {
    formDetails,
    setErrorMessage,
    inputErrors,
  };

  return (
    <>
      {privacyRedirect && (
        <div
          role="status"
          aria-live="polite"
          data-testid="privacy-redirect-notice"
          className="mx-6 mb-4 rounded-md bg-white p-4 text-center text-black sm:mx-10 lg:mx-0"
        >
          {privacyNotice}
        </div>
      )}
      <form
        onSubmit={handleFormSubmit}
        className="mx-6 bg-white p-5 text-black sm:mx-10 lg:mx-0 lg:p-8"
      >
        <div className="lg:flex lg:gap-x-12">
          <div className="lg:flex-1">
            <Explanation />
            <Button
              onClick={() => resetCalculator()}
              className="mb-5 hidden w-full uppercase lg:block"
              variant="secondary"
              type="button"
            >
              {t("reset_button")}
            </Button>
          </div>

          {isDesktopView ? (
            <DesktopInputs {...props} />
          ) : (
            <NonDesktopInputs {...props} />
          )}
        </div>

        <div className="my-8 flex justify-center lg:hidden">
          <Button
            onClick={() => resetCalculator()}
            className="uppercase"
            variant="secondary"
            type="button"
          >
            {t("reset_button")}
          </Button>
        </div>

        <Divider text={t("show_results")} />

        <DetailsInputs register={register} errors={errors} watch={watch} />

        {RECAPTCHA_SITE_KEY && (
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY}
            size="invisible"
            ref={captchaRef}
          />
        )}

        <Button
          variant={isButtonEnabled ? "primary" : "disabled"}
          className="w-full text-center uppercase"
          type="submit"
          data-testid="roi-submit"
        >
          {t("form.results_button")}
        </Button>
      </form>

      <ToastContainer position="top-center" theme="colored" />
    </>
  );
}
